import { BaseAssetVariable, LastValue } from '../common';
import { AssetModelName } from './asset-models.model';
import { AssetVariableThresholds } from './asset-variable-thresholds.model';
import { Asset } from './asset.model';
import { Formula } from './formula.model';

export interface AssetVariable extends BaseAssetVariable {
  asset: Asset;
  followedNumber: number | null;
  formula: Formula;
  lastValue: LastValue | null;
  linked: boolean;
  thresholds: AssetVariableThresholds;
}

export interface AssetVariableCreationModel {
  asset: Asset;
  comment: string;
  followedNumber: number | null;
  formula?: Formula;
  lastValue?: LastValue | null;
  linked: boolean;
  name: string;
  thresholds?: AssetVariableThresholds;
  unit: string;
}

export const NORMALIZED_VARIABLE_NAMES: string[] = [
  'ASSET_AUTONOMY',
  'ASSET_AUTONOMY_STATUS',
  'AUTONOMY',
  'BACKUP_PG_1',
  'BACKUP_PG_2',
  'BACKUP_TANK_1',
  'BACKUP_TANK_2',
  'BACKUP_TANK_3',
  'BACKUP_TANK_4',
  'BACKUP_TANK_5',
  'BACKUP_TANK_6',
  'CO2_RATE',
  'COMPRESSED_AIR_FLOW',
  'CONSUMPTION',
  'CONTRACTUAL_FLOW',
  'CO_RATE',
  'CUMUL_LEVEL',
  'CUMUL_PRESSURE',
  'CUMUL_WEIGHT',
  'DEW_POINT',
  'EMERGENCY_PRESSURE',
  'LEFT_AUTONOMY',
  'LEFT_MINIMUM_PRESSURE',
  'LEFT_PRESSURE',
  'LEFT_VOLUME',
  'LEFT_WEIGHT',
  'LEVEL',
  'MONTHLY_PRODUCTION_DOWNTIME',
  'MONTHLY_PRODUCTION_TIME',
  'MONTHLY_SHORTFALL',
  'MONTHLY_SHUTDOWN_COUNT',
  'MONTHLY_SHUTDOWN_TIME',
  'NETWORK_PRESSURE',
  'PERSONNEL_ON_SITE',
  'PRESSURE',
  'PRODUCTION_FLOW',
  'PRODUCTION_PURITY_A',
  'PRODUCTION_PURITY_B',
  'PRODUCTION_VALVE',
  'PRODUCT_BL_PRESSURE',
  'PRODUCT_BL_PURITY',
  'PRODUCT_BL_TEMPERATURE',
  'RIGHT_AUTONOMY',
  'RIGHT_MINIMUM_PRESSURE',
  'RIGHT_PRESSURE',
  'RIGHT_VOLUME',
  'RIGHT_WEIGHT',
  'RUN_STEP',
  'SHORTFALL',
  'SHUTDOWN_STATUS',
  'TOTAL_FLOW',
  'VAPORIZATION_TEMPERATURE'
].sort((a, b) => (a > b ? 1 : -1));

export interface NormalizedVariable {
  name: string;
  unit: string;
  description: string;
}

export interface NormalizedAssetVariablesByCategory {
  categoryName: AssetModelName | string;
  variables: NormalizedVariable[];
}

export const NORMALIZED_ASSET_VARIABLES: NormalizedAssetVariablesByCategory[] = [
  {
    categoryName: AssetModelName.BULK,
    variables: [
      { name: 'AUTONOMY', unit: 'day', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.AUTONOMY' },
      { name: 'BACKUP_TANK_1', unit: '%', description: '' },
      { name: 'BACKUP_TANK_2', unit: '%', description: '' },
      { name: 'BACKUP_TANK_3', unit: '%', description: '' },
      { name: 'BACKUP_TANK_4', unit: '%', description: '' },
      { name: 'BACKUP_TANK_5', unit: '%', description: '' },
      { name: 'BACKUP_TANK_6', unit: '%', description: '' },
      { name: 'CO2_RATE', unit: 'm3/h', description: '' },
      { name: 'CUMUL_LEVEL', unit: '%', description: '' },
      { name: 'CUMUL_PRESSURE', unit: 'bar', description: '' },
      { name: 'CUMUL_WEIGHT', unit: 'kg', description: '' },
      { name: 'LEVEL', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_DP', unit: 'bar', description: '' },
      { name: 'NETWORK_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.NETWORK_PRESSURE' },
      { name: 'PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'VAPORIZATION_TEMPERATURE', unit: '°C', description: '' }
    ]
  },
  {
    categoryName: AssetModelName.PG,
    variables: [
      { name: 'ASSET_AUTONOMY', unit: 'day', description: '' },
      {
        name: 'ASSET_AUTONOMY_STATUS',
        unit: '/[0]Critical/[1]Critical/[2]Critical/[3]Must Order/[4]Must Order/[5]Must Order/[6]Must Order/[7]Must Order/[8]Should Order/[9]Should Order/[10]Should Order/[11]Should Order/[12]Should Order/Ok',
        description: ''
      },
      { name: 'AUTONOMY', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.AUTONOMY' },
      { name: 'BACKUP_PG_1', unit: 'bar', description: '' },
      { name: 'BACKUP_PG_2', unit: 'bar', description: '' },
      { name: 'CONSUMPTION', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.CONSUMPTION' },
      { name: 'EMERGENCY_PRESSURE', unit: 'bar', description: '' },
      { name: 'LEFT_AUTONOMY', unit: 'day', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEFT_AUTONOMY' },
      { name: 'LEFT_MINIMUM_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEFT_MINIMUM_PRESSURE' },
      { name: 'LEFT_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEFT_PRESSURE' },
      { name: 'LEFT_VOLUME', unit: 'm3', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEFT_VOLUME' },
      { name: 'LEFT_WEIGHT', unit: 'kg', description: '' },
      { name: 'NETWORK_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.NETWORK_PRESSURE' },
      { name: 'RIGHT_AUTONOMY', unit: 'day', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.RIGHT_AUTONOMY' },
      { name: 'RIGHT_MINIMUM_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.RIGHT_MINIMUM_PRESSURE' },
      { name: 'RIGHT_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.RIGHT_PRESSURE' },
      { name: 'RIGHT_VOLUME', unit: 'm3', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.RIGHT_VOLUME' },
      { name: 'RIGHT_WEIGHT', unit: 'kg', description: '' }
    ]
  },
  {
    categoryName: AssetModelName.ONSITE,
    variables: [
      { name: 'BACKUP_TANK_1', unit: '%', description: '' },
      { name: 'BACKUP_TANK_2', unit: '%', description: '' },
      { name: 'BACKUP_TANK_3', unit: '%', description: '' },
      { name: 'BACKUP_TANK_4', unit: '%', description: '' },
      { name: 'BACKUP_TANK_5', unit: '%', description: '' },
      { name: 'BACKUP_TANK_6', unit: '%', description: '' },
      { name: 'CO_RATE', unit: 'ppm', description: '' },
      { name: 'COMPRESSED_AIR_FLOW', unit: 'm3/h', description: '' },
      { name: 'CONTRACTUAL_FLOW', unit: 'ppm', description: '' },
      { name: 'COUNT_SHUTDOWN', unit: '', description: '' },
      { name: 'DEW_POINT', unit: '°C', description: '' },
      { name: 'H_EQPT_Boox', unit: '', description: '' },
      { name: 'H_EQPT_C0x', unit: '', description: '' },
      { name: 'H_EQPT_T0x', unit: '', description: '' },
      { name: 'H_FACILITY', unit: '', description: '' },
      { name: 'H_STDBY', unit: '', description: '' },
      { name: 'H_STP400V', unit: '', description: '' },
      { name: 'H_STPAL', unit: '', description: '' },
      { name: 'H_STPCUST', unit: '', description: '' },
      { name: 'H_STPDFT', unit: '', description: '' },
      { name: 'H_TRANS', unit: '', description: '' },
      { name: 'MONTHLY_PRODUCTION_DOWNTIME', unit: 'h', description: '' },
      { name: 'MONTHLY_PRODUCTION_TIME', unit: 'h', description: '' },
      { name: 'MONTHLY_SHORTFALL', unit: 'scf', description: '' },
      { name: 'MONTHLY_SHUTDOWN_COUNT', unit: '-', description: '' },
      { name: 'MONTHLY_SHUTDOWN_TIME', unit: 'h', description: '' },
      { name: 'MoDerimeTm', unit: '', description: '' },
      { name: 'MoRegenTm', unit: '', description: '' },
      { name: 'MoldleMaTm', unit: '', description: '' },
      { name: 'NETWORK_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.NETWORK_PRESSURE' },
      { name: 'PERSONNEL_ON_SITE', unit: '/Onsite/Offsite', description: '' },
      { name: 'PRODUCT_BL_PRESSURE', unit: 'scfh', description: '' },
      { name: 'PRODUCT_BL_PURITY', unit: 'ppm', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCT_BL_PURITY' },
      { name: 'PRODUCT_BL_TEMPERATURE', unit: '°C', description: '' },
      { name: 'PRODUCTION_FLOW', unit: '/Closed/Open', description: '' },
      { name: 'PRODUCTION_PURITY_A', unit: 'bar', description: '' },
      { name: 'PRODUCTION_PURITY_B', unit: '%', description: '' },
      { name: 'PRODUCTION_VALVE', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE' },
      { name: 'RUN_STEP', unit: '-', description: '' },
      { name: 'SHORTFALL', unit: 'scfh', description: '' },
      { name: 'SHUTDOWN_STATUS', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS' },
      { name: 'TOTAL_FLOW', unit: 'scfh', description: '' }
    ]
  }
];

export enum VariableTypes {
  STANDARD = 'standard',
  CUSTOM = 'custom'
}

export const NORMALIZED_ASSET_GROUP_VARIABLES: NormalizedAssetVariablesByCategory[] = [
  {
    categoryName: AssetModelName.BULK,
    variables: [
      { name: 'AUTONOMY_GROUP', unit: '', description: '' },
      { name: 'LEVEL_1', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_2', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_3', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_4', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_5', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_6', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_7', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_8', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_9', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_10', unit: '%', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.LEVEL' },
      { name: 'LEVEL_GROUP', unit: '%', description: '' },
      { name: 'NETWORK_PRESSURE', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.NETWORK_PRESSURE' },
      { name: 'PRESSURE_1', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_2', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_3', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_4', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_5', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_6', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_7', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_8', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_9', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_10', unit: 'bar', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRESSURE' },
      { name: 'PRESSURE_GROUP', unit: 'bar', description: '' }
    ]
  },
  {
    categoryName: AssetModelName.ONSITE,
    variables: [
      { name: 'AUTONOMY_GROUP', unit: '', description: '' },
      { name: 'PRODUCTION_VALVE_GROUP', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE_GROUP' },
      { name: 'PRODUCTION_VALVE_1', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE' },
      { name: 'PRODUCTION_VALVE_2', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE' },
      { name: 'PRODUCTION_VALVE_3', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE' },
      { name: 'PRODUCTION_VALVE_4', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE' },
      { name: 'PRODUCTION_VALVE_5', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.PRODUCTION_VALVE' },
      { name: 'SHUTDOWN_STATUS_GROUP', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS_GROUP' },
      { name: 'SHUTDOWN_STATUS_1', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS' },
      { name: 'SHUTDOWN_STATUS_2', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS' },
      { name: 'SHUTDOWN_STATUS_3', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS' },
      { name: 'SHUTDOWN_STATUS_4', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS' },
      { name: 'SHUTDOWN_STATUS_5', unit: '', description: 'ASSETS.NORMALIZED_VARIABLES.DESCRIPTIONS.SHUTDOWN_STATUS' }
    ]
  },
  {
    categoryName: AssetModelName.PG,
    variables: [
      { name: 'AUTONOMY_GROUP', unit: '', description: '' },
      { name: 'PRESSURE_GROUP', unit: 'bar', description: '' }
    ]
  }
];

export const FOLLOWED_NUMBERS: number[] = [1, 2, 3];
export const FOLLOWED_NUMBERS_LIST: { label: string; value: number | null }[] = [
  { label: 'None', value: null },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 }
];
